import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import {
  parseAssetsRouteSearch,
  serializeAssetsRouteSearch,
  type AssetsRouteSearch,
} from './assetsRouteSearch';
import {
  defaultAssetsFilter,
  type A2vAssetType,
  type AssetsFilter,
  type AssetsSortProperty,
} from './model';

export function useAssetsRouteFilters() {
  const navigate = useNavigate({ from: '/a2v/assets' });
  const search = useSearch({ from: '/a2v/assets' });
  const filters = useMemo(() => parseAssetsRouteSearch(search), [search]);

  const update = useCallback(
    (updater: (current: AssetsFilter) => AssetsFilter) => {
      void navigate({
        search: (current: AssetsRouteSearch) =>
          serializeAssetsRouteSearch(updater(parseAssetsRouteSearch(current))),
        replace: true,
      });
    },
    [navigate],
  );
  const setSearch = useCallback(
    (searchTerm: string) => {
      update((current) => ({ ...current, searchTerm, page: defaultAssetsFilter.page }));
    },
    [update],
  );
  const setAssetType = useCallback(
    (assetType: A2vAssetType | null) => {
      update((current) => ({ ...current, assetType, page: defaultAssetsFilter.page }));
    },
    [update],
  );
  const setTags = useCallback(
    (tags: string[]) => {
      update((current) => ({ ...current, tags, page: defaultAssetsFilter.page }));
    },
    [update],
  );
  const setPagination = useCallback(
    (page: number, size: number) => {
      update((current) => ({
        ...current,
        page: size === current.size ? page : defaultAssetsFilter.page,
        size,
      }));
    },
    [update],
  );
  const setSort = useCallback(
    (sortProperty: AssetsSortProperty | null, sortDirection: AssetsFilter['sortDirection']) => {
      update((current) => ({ ...current, sortProperty, sortDirection, page: 0 }));
    },
    [update],
  );

  return {
    filters,
    setSearch,
    setAssetType,
    setTags,
    setPagination,
    setSort,
  };
}
